import React, { useState, useEffect } from "react";
import styled from "styled-components/macro";
import { COLORS } from "../../assets/fansaves-ui";
import { withFirebase } from "../Firebase";
import { withAuthentication } from "../Session";
import { checkAndSuggestUsernames } from "../../utilities/user_helpers";

import { FormGroup, Label, Input } from "reactstrap";
import UsernameSuggestions from "../../assets/fansaves-ui/UsernameSuggestions";
import { Button } from "../../containers/UIKit";
import ErrorMessage from "../../assets/fansaves-ui/OnboardingForms/ErrorMessage";
import { Icon } from "@iconify/react";

const FormContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  flex: 1;
`;

const InputContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  gap: 10px;
`;

const StyledLabel = styled(Label)`
  font-size: 18px;
  font-weight: 400;
  font-family: "Manrope", sans-serif;
  color: ${COLORS.primaryBlack};
  padding-left: 10px;
`;

const StyledInput = styled(Input)`
  font-size: 16px;
  font-weight: 400;
  font-family: "Manrope", sans-serif;
  color: ${COLORS.primaryBlack};
  border: 1px solid ${COLORS.primaryBlue};
  border-color: ${({ error }) =>
    error ? COLORS.errorRed : COLORS.primaryBlue};
  border-radius: 10px;
  padding: 10px 20px;
  width: 100%;

  &:focus {
    border-color: ${COLORS.primaryBlueHover};
    outline: none;
  }

  &:focus-visible {
    outline: none;
  }

  &:disabled {
    background-color: transparent;
    color: ${COLORS.darkGrey};
  }
`;

const EditIcon = styled(Icon)`
  color: ${COLORS.primaryBlue};
`;

const EditIconContainer = styled.div`
  margin-top: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 7px;
  background-color: #cfe6ff;
  border-radius: 35px;
  cursor: pointer;

  &:hover {
    background-color: #b3d6ff;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 90%;
  gap: 10px;
  margin-top: 10px;
`;

const StyledButtonTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  font-family: "Manrope", sans-serif;
`;

const InputWithErrorContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const UsernameEdit = ({ authUser, firebase }) => {
  const [editMode, setEditMode] = useState(false);
  const [userName, setUserName] = useState("");
  const [error, setError] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (authUser) {
      setUserName(authUser.userName || "");
    }
  }, [authUser]);

  const handleUsernameCheck = () => {
    if (userName && userName.trim().length > 0) {
      return new Promise((resolve, reject) => {
        checkAndSuggestUsernames(
          firebase,
          userName,
          (response) => {
            if (!response) {
              reject();
            } else {
              if (!response.available && response.suggestions) {
                setSuggestions(response.suggestions);
              }
            }
            resolve(response);
          },
          (error) => {
            reject(error);
          }
        );
      });
    } else {
      return Promise.resolve();
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!userName || userName.trim() === "") {
      setError("Username is required");
      return;
    }

    const usernameCheckResponse = await handleUsernameCheck();
    if (!usernameCheckResponse || !usernameCheckResponse.available) {
      setError("This username is already taken. Please choose another.");
      return;
    } else {
      setError("");
    }

    try {
      setSubmitting(true);
      const userPath = `/user/${authUser.key}`;
      const userRef = firebase.database.ref(userPath);

      await userRef.update({ userName });
      setSubmitting(false);
      setEditMode(false);
      setError("");
      setSuggestions([]);
    } catch (error) {
      setSubmitting(false);
      setError("Error updating username. Please try again.");
      console.error("Error updating username", error);
    }
  };

  const handleCancel = () => {
    setUserName(authUser.userName);
    setEditMode(false);
    setError("");
    setSuggestions([]);
  };

  return (
    <FormContainer>
      <FormGroup style={{ width: "100%" }}>
        <StyledLabel>Username</StyledLabel>
        <InputContainer>
          <InputWithErrorContainer>
            <StyledInput
              name="userName"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              placeholder="Username"
              onFocus={() => setError("")}
              error={error}
              disabled={!editMode}
              onBlur={handleUsernameCheck}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleUpdate(e);
                }
              }}
            />
            {error && <ErrorMessage error={error} />}
          </InputWithErrorContainer>
          <EditIconContainer onClick={() => setEditMode(!editMode)}>
            <EditIcon icon="fa-solid:pencil-alt" width={17} />
          </EditIconContainer>
        </InputContainer>
        {suggestions.length > 0 && (
          <UsernameSuggestions
            suggestions={suggestions}
            updateUsername={(suggestion) => setUserName(suggestion)}
            updateSuggestions={(suggestions) => setSuggestions(suggestions)}
            updateError={(error) => setError(error)}
          />
        )}
        {editMode && (
          <ButtonContainer>
            <Button
              className="green-outline-continue rounded-10 medium"
              onClick={handleCancel}
              disabled={submitting}
            >
              <StyledButtonTitle>Cancel</StyledButtonTitle>
            </Button>
            <Button
              className={`green-continue rounded-10 medium ${
                (submitting || !userName || userName.trim() === "") &&
                "disabled"
              }`}
              onClick={handleUpdate}
              disabled={submitting}
            >
              <StyledButtonTitle>Save</StyledButtonTitle>
            </Button>
          </ButtonContainer>
        )}
      </FormGroup>
    </FormContainer>
  );
};

export default withFirebase(withAuthentication(UsernameEdit));
